import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Trophy, ArrowBigUp, Share2 } from "lucide-react";
import api from "@/lib/api";
import { ASSETS } from "@/lib/assets";
import BadgeTag from "@/components/BadgeTag";
import Confetti from "@/components/Confetti";

const SHARE_BASE = "https://robbedcoin.com";

function shareChampion(w) {
  const text = `👑 THE CREW HAS SPOKEN. @${w.winner.username} is the $ROBBED Champion of Battle #${String(w.number || 1).padStart(3, "0")} with ${w.winner.votes} community votes! THEY GOT RICH. WE GOT ROBBED.`;
  const url = `${SHARE_BASE}/hall-of-fame`;
  window.open(`https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}&via=robbedcoin`,
    "_blank", "noopener,noreferrer,width=600,height=500");
}

export default function HallOfFame() {
  const { data: winners = [], isLoading } = useQuery({
    queryKey: ["winners"], queryFn: async () => (await api.get("/battles/winners")).data,
  });
  const champ = winners[0];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
      <span className="tag bg-gold text-black">Champions</span>
      <h1 className="headline text-cream text-4xl sm:text-6xl mt-5">HALL OF <span className="text-gold">FAME</span></h1>
      <p className="font-mono text-dim mt-4 max-w-xl">The memes that robbed the hardest. Meme Battle champions, immortalized. Recognition and bragging rights only — no financial rewards.</p>

      {champ && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          className="comic-card border-gold p-5 sm:p-8 mt-8 relative overflow-hidden" data-testid="weekly-champion">
          <div className="halftone absolute inset-0 opacity-20" />
          <Confetti key={champ.battleId} count={26} spread={220} className="left-1/2 top-4 -translate-x-1/2" />
          <div className="relative flex flex-col sm:flex-row gap-6 items-center">
            <img src={`${process.env.REACT_APP_BACKEND_URL}${champ.winner.image_url}`} alt="champion meme"
              className="w-full sm:w-64 max-h-72 object-contain border-3 border-black bg-white shrink-0" />
            <div className="flex-1 text-center sm:text-left">
              <span className="flex items-center justify-center sm:justify-start gap-1.5 text-gold font-display font-900 uppercase text-sm">
                <Trophy className="w-5 h-5" /> This Week's ROBBED Champion
              </span>
              <h2 className="font-display font-900 text-cream text-2xl sm:text-3xl uppercase mt-2">{champ.title}</h2>
              <div className="flex items-center justify-center sm:justify-start gap-2 mt-3">
                {champ.winner.user_id
                  ? <Link to={`/u/${champ.winner.user_id}`} className="font-mono text-neon hover:underline">@{champ.winner.username}</Link>
                  : <span className="font-mono text-dim">@{champ.winner.username}</span>}
                <BadgeTag rob={champ.winner.rob} />
              </div>
              <p className="font-display font-900 text-neon text-xl mt-2">🔥 {champ.winner.votes} COMMUNITY VOTES</p>
              <p className="font-heading font-bold uppercase text-dim text-sm mt-1">"The crew has spoken."</p>
              <button onClick={() => shareChampion(champ)} className="btn-neon mt-4 !py-2 !px-4 !text-xs inline-flex" data-testid="share-champion-x-btn">
                <Share2 className="w-4 h-4" /> Share The Champion
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {isLoading ? (
        <p className="font-mono text-dim mt-10">Loading legends...</p>
      ) : winners.length === 0 ? (
        <div className="flex flex-col items-center py-20 text-center">
          <img src={ASSETS.pointing} alt="empty" className="w-40 animate-floaty" />
          <p className="font-display text-cream mt-4 uppercase">No champions crowned yet.</p>
          <Link to="/battles" className="btn-neon mt-6">Enter A Battle</Link>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
          {winners.map((w, i) => (
            <motion.div key={w.battleId} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="comic-card p-5 border-gold" data-testid={`champion-${w.battleId}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] text-dim">BATTLE #{String(w.number || 1).padStart(3, "0")}</span>
                <span className="flex items-center gap-1 text-gold font-display font-900 uppercase text-xs"><Trophy className="w-4 h-4" /> Champion</span>
              </div>
              <h3 className="font-display font-900 text-cream uppercase text-lg mt-2">{w.title}</h3>
              <img src={`${process.env.REACT_APP_BACKEND_URL}${w.winner.image_url}`} alt="winning meme" className="mt-3 w-full max-h-72 object-contain border-3 border-black bg-white" />
              <div className="flex items-center justify-between mt-3">
                <div className="flex items-center gap-2 min-w-0">
                  {w.winner.user_id
                    ? <Link to={`/u/${w.winner.user_id}`} className="font-mono text-xs text-cream hover:text-neon truncate">@{w.winner.username}</Link>
                    : <span className="font-mono text-xs text-cream truncate">@{w.winner.username}</span>}
                  <BadgeTag rob={w.winner.rob} />
                </div>
                <span className="flex items-center gap-1 font-display font-900 text-neon"><ArrowBigUp className="w-4 h-4" /> {w.winner.votes}</span>
              </div>
              <button onClick={() => shareChampion(w)} className="btn-ghost mt-3 w-full !py-1.5 !text-[11px] inline-flex justify-center" data-testid={`share-champion-${w.battleId}`}>
                <Share2 className="w-3.5 h-3.5" /> Share To X
              </button>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
